/**
 * CalendarDayAgenda — the Day view: the Practice calendar's day list as its agenda table (PM `day-list-calendar.tsx`
 * `AgendaBody` / `AgendaRow`, origin/main 5a49b124; the designer's ruling 21 Oct: PM is the reference for look and
 * behaviour). The page draws it for every Day, whatever the `dayAgenda` switch says (PROD-3307 C5).
 *
 * What it draws, as PM:
 *   - a pinned header row: the live clock and the zone in the corner (PM `GutterClock`, the DS `PACalendarGutterClock`)
 *     and the seven column labels;
 *   - every hour of the range: the hour on the gutter, a hairline across (blue on the current hour, the now line, with the
 *     hour on a blue pill); an hour with nothing booked keeps one row's height, and the current one says so in words;
 *   - one row per booking: time and room, patient with avatar, DOB and age, doctor, profile and reason for visit, the
 *     status (Antonin's export, 21 Oct: the soft status chip with its name), the note (one line, the whole note in a
 *     tooltip on hover, the export's `handoff-calendar-day.md` §5), then the row's actions; tinted by its LOCATION (PM
 *     `locationTileBg`) with a slim bar in the location's colour (the visit type's when there is no location, PM
 *     `locationEdgeColor`); past rows grey and muted; a block as a striped row.
 *
 * Row actions, as PM: every action of the row's status (`getRowActionsFor`: arrive, no show, check in, unmark,
 * reschedule, confirm, cancel, edit, follow-up) is an inline neutral icon button with its tooltip, at most three of them
 * (Antonin's export, 22 Oct: the rest live under the ⋯, at the top of its menu, through the same handler), the status
 * shortcuts through `onStatusChange` behind the Mark-Arrived guard, the others through `onAction`; then the ⋯ menu with
 * PM's items (note, edit, PVS, Billing tab, recurring series, cancel). Kept from the PA (its clinical purpose, not a calendar-UI
 * choice): "Open encounter" — a signed-in practitioner's own visit that the visit gate lets open (`canOpenEncounter`)
 * carries the row's one primary button, FIRST in its actions, firing the row's `open-encounter` action — and the ⋯
 * menu's "Open encounter" item, and "Proceed to complete patient" opening the appointment page.
 *
 * Real data only: the same `DayListRow`s, the same props (the lift seams too). Look: the `.cal-day-*` rules in the
 * bridge ("calendar day agenda", "calendar: open encounter"). Graduation candidate: the DS day list.
 */
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import {
  Ban,
  Flag,
  MessageSquare,
  MoreHorizontal,
  Repeat,
  Stethoscope,
  type LucideIcon,
} from 'lucide-react';
import { APPT_GLYPH, locationGlyph, ROW_ACTION_GLYPH as ROW_ACTION_ICON, type ApptGlyph } from '@/scheduling/lib/appointment-glyphs';
import {
  AnchoredPopover,
  Button,
  AnchoredPopoverContent,
  AnchoredPopoverTrigger,
  Avatar,
  BLOCK_STRIPES,
  ModalYesNo,
  PACalendarGutterClock,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  type PACalendarDayListProps,
  type PACalendarDayListRow,
} from '@scripsteam/scrips-signal-ds';
import { categoryHue, type CategoryHue } from '@/shared/lib/category-color';
import { formatHourMark } from '@/shared/lib/time-format';
import { isPracticeToday, practiceNow } from '@/shared/lib/practice-time';
import { cn } from '@/lib/utils';
import { SoftStatusChip } from '@/shared/ui/components/status-chip/soft-status-chip';
import { dayStatusChip } from '@/scheduling/lib/day-status-chip';
import {
  getRowActionsFor,
  shouldShowBillingTabMenuItem,
  shouldShowOpenEncounterMenuItem,
  shouldShowPvsMenuItem,
  shouldShowViewRecurringSeriesMenuItem,
  statusCodeForAction,
  type AppointmentAction,
  type AppointmentActionId,
} from '@/scheduling/lib/appointment-actions';
import {
  ARRIVED_GUARD_PROCEED,
  ARRIVED_GUARD_TITLE,
  arrivedGuardMessage,
  arrivedGuardStatus,
  type ArrivedGuardKind,
} from '@/scheduling/lib/arrived-guard';
import { canOpenEncounter, gateStatusOf } from '@/scheduling/lib/visit-gate';
import { useVisitGate } from '@/scheduling/lib/visit-gate-flag';

export type CalendarDayAgendaProps = Pick<
  PACalendarDayListProps,
  | 'rows'
  | 'hours'
  | 'currentHour'
  | 'selectedDateISO'
  | 'onAppointmentClick'
  | 'onAppointmentAction'
  | 'onStatusChange'
  | 'statusUpdatePending'
  | 'canViewBillingTab'
> & {
  /** The appointment whose status change is in flight: only its row disables (PM). Absent: the list-wide flag. */
  statusUpdatePendingId?: string;
  /** Bumped to scroll again to now / the first booking ("Today" pressed while already on today). */
  scrollRequest?: number;
  /** The signed-in practitioner's id: only their own visits carry "Open encounter". Absent: no row carries it. */
  selfPractitionerId?: string;
  /** "Proceed to complete patient" in the Arrived dialog: open the appointment page on what is missing. */
  onCompletePatient?: (row: PACalendarDayListRow) => void;
};

type Row = PACalendarDayListRow;

/** PM `scheduling/lib/location-colors.ts` `hueOf`: the location's hue (in clinic jade, home orange, virtual sky; jade
 *  when unknown, the list API carrying the room name only). */
function locationHue(location?: string): CategoryHue {
  if (!location) return 'jade';
  const key = location.toLowerCase();
  const exact: Record<string, CategoryHue> = { office: 'jade', home: 'orange', virtual: 'sky', block: 'purple' };
  if (exact[key]) return exact[key];
  if (key.includes('virtual') || key.includes('video') || key.includes('tele')) return 'sky';
  if (key.includes('home')) return 'orange';
  return 'jade';
}

/** PM `locationTileBg(row.profileLocation)`: the row's tint is its location's hue. */
function rowHue(row: Row): CategoryHue {
  return locationHue(row.profileLocation);
}

/** PM `locationEdgeColor(row.profileLocation, row.profileColor)`: the bar takes the location's hue, the visit type's when
 *  there is no location (jade when neither reads). */
function edgeHue(row: Row): CategoryHue {
  if (row.profileLocation) return locationHue(row.profileLocation);
  return categoryHue(row.profileColor) ?? 'jade';
}

/** The row's ISO is a wall-clock time; the hour is read as the DS list and the hour range read it. */
/** At most this many action icons sit inline on a Day row; the rest are under the ⋯ (Antonin's export, 22 Oct). */
export const MAX_INLINE_ROW_ACTIONS = 3;

function hourOf(row: Row): number {
  return new Date(row.startISO).getHours();
}

function isSameDay(a: string, b: string): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}

export function CalendarDayAgenda({
  rows,
  hours,
  currentHour,
  selectedDateISO,
  onAppointmentClick,
  onAppointmentAction,
  onStatusChange,
  statusUpdatePending = false,
  statusUpdatePendingId,
  canViewBillingTab = false,
  scrollRequest = 0,
  selfPractitionerId,
  onCompletePatient,
}: CalendarDayAgendaProps) {
  // Read once per mount, as the popup reads it.
  const visitGateOn = useVisitGate();
  // "Open encounter": the signed-in practitioner's own visit that the visit gate lets open now.
  const canOpenRow = (row: Row) =>
    !!selfPractitionerId &&
    row.practitionerId === selfPractitionerId &&
    canOpenEncounter(gateStatusOf(row.statusCode), new Date(row.startISO), visitGateOn);
  // PM's actions column fits five icon buttons; "Open encounter" needs its own room, only while a row shows it.
  const anyOpenEncounter = rows.some((r) => !r.isBlock && canOpenRow(r));
  const byHour = new Map<number, Row[]>();
  for (const r of rows) {
    const h = hourOf(r);
    const list = byHour.get(h);
    if (list) list.push(r);
    else byHour.set(h, [r]);
  }
  // Every hour of the range, empty ones included; a booking outside the range still gets its own hour line.
  const shownHours = [...hours];
  for (const h of byHour.keys()) if (!shownHours.includes(h)) shownHours.push(h);
  shownHours.sort((a, b) => a - b);

  // Opens on now, as the Practice calendar's day list (PM `day-list-calendar.tsx`): on today only (a `currentHour`), the
  // current hour's line goes 24 below the sticky head (PM `scrollMarginTop: 64` = the 40 head + 24), instantly, once per
  // (day, current hour, row count), so a booking added or removed scrolls it back to now; another day is not scrolled.
  // `scrollRequest` ("Today" while already on today) asks again.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (currentHour === undefined || hours.length === 0) return;
    const key = `${selectedDateISO}|${currentHour}|${rows.length}|${scrollRequest}`;
    if (scrolledFor.current === key) return;
    // One frame for the layout to settle after the render commits (PM: 50 ms).
    const id = window.setTimeout(() => {
      const scroller = scrollerRef.current;
      const target = bodyRef.current?.querySelector<HTMLElement>(`[data-hour="${currentHour}"]`);
      if (!scroller || !target) return;
      const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      const top = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - margin;
      if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      else scroller.scrollTop = Math.max(0, top);
      scrolledFor.current = key;
    }, 50);
    return () => window.clearTimeout(id);
  }, [currentHour, hours.length, rows.length, selectedDateISO, scrollRequest]);

  return (
    <div data-slot="pa-calendar-day-agenda" className="cal-day" data-open-encounter-col={anyOpenEncounter ? '' : undefined}>
      <div ref={scrollerRef} className="cal-day-scroll scrollbar-hide">
        <div className="cal-day-sheet">
          <div className="cal-day-head">
            <div className="cal-day-gutter cal-day-corner">
              <PACalendarGutterClock />
            </div>
            <div className="cal-day-cols cal-day-labels">
              <div data-col="time">Time (room)</div>
              <div data-col="patient">Patient</div>
              <div data-col="dob">DOB</div>
              <div data-col="doctor">Doctor</div>
              <div data-col="profile">Profile (RFV)</div>
              <div data-col="status">Status</div>
              <div data-col="note">Note</div>
              <div data-col="actions" />
            </div>
          </div>
          <div ref={bodyRef} className="cal-day-body">
            {shownHours.map((hour) => {
              const apts = byHour.get(hour) ?? [];
              const isCurrent = hour === currentHour;
              return (
                <section key={hour} data-hour={hour} aria-label={formatHourMark(hour)} className="cal-day-hour">
                  <div className="cal-day-hour-line" data-current={isCurrent ? '' : undefined}>
                    <div className="cal-day-gutter">
                      <span data-hour-marker={isCurrent ? 'current' : undefined} className="cal-day-hour-marker">
                        {formatHourMark(hour)}
                      </span>
                    </div>
                    <div className="cal-day-rule" />
                  </div>
                  {apts.map((row) => (
                    <AgendaRow
                      key={row.id}
                      row={row}
                      selectedDateISO={selectedDateISO}
                      onClick={onAppointmentClick}
                      onAction={onAppointmentAction}
                      onCompletePatient={onCompletePatient}
                      onStatusChange={onStatusChange}
                      statusUpdatePending={
                        statusUpdatePendingId !== undefined ? statusUpdatePendingId === row.id : statusUpdatePending
                      }
                      canViewBillingTab={canViewBillingTab}
                      canOpen={canOpenRow(row)}
                    />
                  ))}
                  {apts.length === 0 ? (
                    // An empty hour keeps one row's height, so the day reads at a steady rhythm; the current hour says so.
                    <div className="cal-day-empty" data-empty-hour>
                      <div className="cal-day-gutter" />
                      {isCurrent ? <p>Nothing booked this hour</p> : null}
                    </div>
                  ) : null}
                </section>
              );
            })}
            {rows.length === 0 ? <p className="cal-day-none">No appointments scheduled for this day</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

interface AgendaRowProps {
  row: Row;
  selectedDateISO?: string;
  onClick?: (id: string) => void;
  onAction?: (row: Row, action: AppointmentActionId) => void;
  onCompletePatient?: (row: Row) => void;
  onStatusChange?: (row: Row, statusCode: string) => void;
  statusUpdatePending: boolean;
  canViewBillingTab: boolean;
  /** The visit can be opened now by the signed-in practitioner: the row shows "Open encounter". */
  canOpen: boolean;
}

function AgendaRow({ row, selectedDateISO, onClick, onAction, onCompletePatient, onStatusChange, statusUpdatePending, canViewBillingTab, canOpen }: AgendaRowProps) {
  if (row.isBlock) {
    const label = row.note?.trim() || 'Blocked';
    return (
      <div className="cal-day-line">
        <div className="cal-day-gutter" />
        <div onClick={() => onClick?.(row.id)} className={cn('cal-day-block', BLOCK_STRIPES)} data-agenda-block>
          {row.isRecurring ? (
            <Repeat className="cal-day-glyph" aria-label="Recurring block" />
          ) : (
            <Ban className="cal-day-glyph" aria-hidden />
          )}
          <span className="cal-day-block-title">Blocked</span>
          {label !== 'Blocked' ? <span className="cal-day-block-note">— {label}</span> : null}
          <span className="cal-day-block-time">
            {row.timeRange}
            {row.room ? ` · ${row.room}` : ''}
          </span>
        </div>
      </div>
    );
  }
  const isPast = new Date(row.endISO).getTime() < practiceNow().getTime();
  return (
    <div className="cal-day-line">
      <div className="cal-day-gutter" />
      <div
        onClick={() => onClick?.(row.id)}
        className="cal-day-cols cal-day-row"
        data-agenda-row
        data-hue={rowHue(row)}
        data-edge-hue={edgeHue(row)}
        data-past={isPast ? '' : undefined}
      >
        <span aria-hidden data-event-edge className="cal-day-edge" />
        <TimeCell row={row} />
        <PatientCell row={row} />
        <DobCell row={row} />
        <DoctorCell row={row} />
        <ProfileCell row={row} />
        <StatusCell row={row} />
        <NoteCell row={row} />
        <ActionsCell
          row={row}
          selectedDateISO={selectedDateISO}
          onAction={onAction}
          onCompletePatient={onCompletePatient}
          onStatusChange={onStatusChange}
          statusUpdatePending={statusUpdatePending}
          canViewBillingTab={canViewBillingTab}
          canOpen={canOpen}
        />
      </div>
    </div>
  );
}

function TimeCell({ row }: { row: Row }) {
  const location = row.profileLocation;
  const LocationGlyph = locationGlyph(location);
  return (
    <div className="cal-day-time" data-col="time">
      <div className="cal-day-marks">
        <LocationGlyph className="size-3" aria-hidden />
        {row.isRecurring ? <Repeat className="size-3" aria-hidden /> : null}
        {row.hasFlag ? <Flag className="size-3" aria-hidden /> : null}
        {row.hasNote ? <MessageSquare className="size-3" aria-hidden /> : null}
      </div>
      <div className="cal-day-cell">
        <div className="cal-day-primary cal-day-nowrap">{row.timeRange}</div>
        {row.room ? (
          <div className="cal-day-secondary" title={row.room}>
            {row.room}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function PatientCell({ row }: { row: Row }) {
  return (
    <div className="cal-day-person" data-col="patient">
      <Avatar scale="compact" tone="patient" name={row.patientName} src={row.patientAvatar} importance={row.patientImportance} size="md" />
      <div className="cal-day-cell cal-day-name">
        <span className="cal-day-primary" title={row.patientName}>
          {row.patientName}
        </span>
        {row.patientBadge ? <span className="cal-day-badge">{row.patientBadge}</span> : null}
      </div>
    </div>
  );
}

function DobCell({ row }: { row: Row }) {
  if (!row.dob) return <div data-col="dob" />;
  return (
    <div className="cal-day-cell" data-col="dob">
      <div className="cal-day-body-text">{row.dob}</div>
      {row.age ? <div className="cal-day-secondary">{row.age}</div> : null}
    </div>
  );
}

function DoctorCell({ row }: { row: Row }) {
  if (!row.doctorName) return <div data-col="doctor" />;
  return (
    <div className="cal-day-person" data-col="doctor">
      <Avatar scale="compact" tone="provider" name={row.doctorName} src={row.doctorPhoto} size={24} />
      <div className="cal-day-cell">
        <div className="cal-day-primary" title={row.doctorName}>
          {row.doctorName}
        </div>
        {row.doctorSpecialty ? (
          <div className="cal-day-secondary" title={row.doctorSpecialty}>
            {row.doctorSpecialty}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ProfileCell({ row }: { row: Row }) {
  if (!row.profileName && !row.reasonForVisit) return <div data-col="profile" />;
  return (
    <div className="cal-day-cell" data-col="profile">
      {row.profileName ? (
        <div className="cal-day-body-text" title={row.profileName}>
          {row.profileName}
        </div>
      ) : null}
      {row.reasonForVisit ? (
        <div className="cal-day-secondary" title={row.reasonForVisit}>
          {row.reasonForVisit}
        </div>
      ) : null}
    </div>
  );
}

/** The row's status as Antonin's export draws it (`docs/handoff-calendar-day.md` §4, `CalendarScreen.tsx`
 *  `enhanceDayList`): the soft status chip with the status circle and the status name (22 Oct), 4 px radius, word and
 *  circle from the API code (`dayStatusChip`). Every status sits in the grey neutral chip (Antonin, 1 Nov); the circle
 *  and the word tell the statuses apart. */
function StatusCell({ row }: { row: Row }) {
  const { label, glyph } = dayStatusChip(row);
  return (
    <div className="cal-day-status" data-col="status">
      <SoftStatusChip semantic="neutral" glyph={glyph}>
        {label}
      </SoftStatusChip>
    </div>
  );
}

/** The note as Antonin's export draws it (23 Oct, `CalendarScreen.tsx` `enhanceDayList` and `encounter-overrides.css`
 *  `[data-day-note-card]`): its text in the column, two lines, then an ellipsis; hovering the cell opens a note card
 *  ("Appointment note" and the whole note, nothing else; white, a hairline, no shadow) under the note, its right edge on
 *  the cell's, above it when there is no room below. The card portals out of the row, so the row never clips it. An
 *  empty or whitespace-only note leaves the column empty. */
function NoteCell({ row }: { row: Row }) {
  const note = row.note?.trim();
  if (!note) return <div data-col="note" />;
  return (
    <Tooltip>
      <TooltipTrigger
        delay={0}
        closeDelay={0}
        render={
          // Focusable, so the card opens on keyboard focus too (there is no native title).
          <span className="cal-day-note" data-col="note" data-slot="pa-calendar-day-note" tabIndex={0}>
            {note}
          </span>
        }
      />
      <TooltipContent side="bottom" align="end" className="cal-day-note-tip">
        <div className="cal-day-note-tip-label">Appointment note</div>
        <div className="cal-day-note-tip-body">{note}</div>
      </TooltipContent>
    </Tooltip>
  );
}

function ActionsCell({
  row,
  selectedDateISO,
  onAction,
  onCompletePatient,
  onStatusChange,
  statusUpdatePending,
  canViewBillingTab,
  canOpen,
}: {
  row: Row;
  selectedDateISO?: string;
  onAction?: (row: Row, action: AppointmentActionId) => void;
  onCompletePatient?: (row: Row) => void;
  onStatusChange?: (row: Row, statusCode: string) => void;
  statusUpdatePending: boolean;
  canViewBillingTab: boolean;
  canOpen: boolean;
}) {
  // Mark-Arrived incomplete-patient-data guard (PROD-989), the one the DS list runs.
  const [arrivedAlertKind, setArrivedAlertKind] = useState<ArrivedGuardKind>(null);
  const isToday = selectedDateISO ? isSameDay(row.startISO, selectedDateISO) : isPracticeToday(row.startISO);
  const actions = getRowActionsFor({
    statusCode: row.statusCode,
    isToday,
    isHomeLocation: (row.locationName ?? '').toLowerCase() === 'home',
    isRecurring: row.isRecurring,
  });
  if (actions.length === 0 && !canOpen) return <div data-col="actions" />;
  // At most three icons inline; the rest go under the ⋯ (no action lost), which every row with more than three has.
  const iconActions = actions.filter((a) => a.id !== 'overflow');
  const inlineActions = iconActions.slice(0, MAX_INLINE_ROW_ACTIONS);
  const foldedActions = iconActions.slice(MAX_INLINE_ROW_ACTIONS);
  const hasOverflow = foldedActions.length > 0 || actions.some((a) => a.id === 'overflow');

  const handle = (action: AppointmentAction) => (e: MouseEvent) => {
    e.stopPropagation();
    const newCode = statusCodeForAction(action.id);
    if (newCode) {
      if (action.id === 'mark-arrived') {
        const { blocked, kind } = arrivedGuardStatus({
          identificationId: row.patientIdentificationId,
          guardianIdentificationId: row.guardianIdentificationId,
          guardianRelationId: row.guardianRelationId,
          birthDateISO: row.patientBirthDateISO,
          isMissingData: row.patientIsMissingData,
        });
        if (blocked) {
          setArrivedAlertKind(kind);
          return;
        }
      }
      onStatusChange?.(row, newCode);
      return;
    }
    onAction?.(row, action.id);
  };

  return (
    <div className="cal-day-actions" data-col="actions">
      {canOpen ? (
        <Button
          variant="primary"
          size="sm"
          data-open-encounter
          icon={<Stethoscope className="size-4" aria-hidden />}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onAction?.(row, 'open-encounter');
          }}
        >
          Open encounter
        </Button>
      ) : null}
      {/* PM `ActionsCell`: each of the row's actions inline, a neutral icon button with its tooltip; ⋯ is the menu. */}
      <TooltipProvider delay={0} closeDelay={0}>
        {inlineActions.map((action) => (
          <ActionButton
            key={action.id}
            id={action.id}
            label={action.label}
            onClick={handle(action)}
            disabled={statusUpdatePending}
          />
        ))}
        {hasOverflow ? (
          <OverflowMenu
            row={row}
            onAction={onAction}
            canViewBillingTab={canViewBillingTab}
            folded={foldedActions.map((action) => ({ action, onClick: handle(action) }))}
            foldedDisabled={statusUpdatePending}
          />
        ) : null}
      </TooltipProvider>
      <ModalYesNo
        open={arrivedAlertKind !== null}
        onCancel={() => setArrivedAlertKind(null)}
        title={ARRIVED_GUARD_TITLE}
        message={arrivedGuardMessage(arrivedAlertKind)}
        cancelLabel="Cancel"
        confirmLabel={ARRIVED_GUARD_PROCEED}
        onConfirm={() => {
          setArrivedAlertKind(null);
          if (onCompletePatient) onCompletePatient(row);
          else onAction?.(row, 'edit');
        }}
      />
    </div>
  );
}

/** PM `ActionButton`: a neutral 32 px icon frame, its Lucide glyph, the action's name as the tooltip and the label. */
function ActionButton({
  id,
  label,
  onClick,
  disabled,
}: {
  id: AppointmentActionId;
  label: string;
  onClick: (e: MouseEvent) => void;
  disabled?: boolean;
}) {
  const Glyph = ROW_ACTION_ICON[id];
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button type="button" aria-label={label} onClick={onClick} disabled={disabled} className="cal-row-action" data-row-action={id} />
        }
      >
        {Glyph ? <Glyph className="cal-row-action-icon" aria-hidden /> : <span aria-hidden>{label.charAt(0)}</span>}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** PM `OverflowMenu`, status-aware: note, edit, PVS, Billing tab, recurring series, cancel; plus the PA's "Open
 *  encounter" item (kept function). `folded` are the row's actions past the three inline icons, first in the menu, each
 *  through the row's own handler (the status ones behind the Mark-Arrived guard), in their row order; one the menu
 *  also offers (Cancel, Edit) leads with the folded ones and is not drawn a second time lower down. */
function OverflowMenu({
  row,
  onAction,
  canViewBillingTab,
  folded = [],
  foldedDisabled = false,
}: {
  row: Row;
  onAction?: (row: Row, action: AppointmentActionId) => void;
  canViewBillingTab: boolean;
  folded?: { action: AppointmentAction; onClick: (e: MouseEvent) => void }[];
  foldedDisabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  /** The folded actions lead the menu in their row order; the menu's own Edit / Cancel are dropped when one is among them. */
  const foldedIds = new Set<AppointmentActionId>(folded.map(({ action }) => action.id));
  const code = (row.statusCode ?? '').toLowerCase();
  const isTerminal = code === 'cancelled' || code === 'canceled' || code === 'noshow' || code === 'no-show' || code === 'fulfilled';
  const handle = (action: AppointmentActionId) => (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onAction?.(row, action);
  };
  return (
    <AnchoredPopover open={open} onOpenChange={setOpen}>
      {/* PM: the ⋯ has no tooltip, only its accessible name. */}
      <AnchoredPopoverTrigger
        render={<button type="button" aria-label="More actions" onClick={(e) => e.stopPropagation()} className="cal-row-action" />}
      >
        <MoreHorizontal className="cal-row-action-icon" aria-hidden />
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent align="end" sideOffset={4} className="cal-day-menu">
        {folded.map(({ action, onClick }) => (
            <MenuItem
              key={action.id}
              glyph={ROW_ACTION_ICON[action.id]}
              label={action.label}
              tone={action.id === 'cancel' ? 'error' : 'default'}
              disabled={foldedDisabled}
              folded
              onClick={(e) => {
                setOpen(false);
                onClick(e);
              }}
            />
        ))}
        <MenuItem icon="comment" label={row.note ? 'Edit note' : 'Add note'} onClick={handle('add-note')} />
        {foldedIds.has('edit') ? null : <MenuItem icon="edit" label="Edit Appointment" onClick={handle('edit')} />}
        {shouldShowOpenEncounterMenuItem(row.statusCode) ? (
          <MenuItem glyph={ROW_ACTION_ICON['open-encounter']} label="Open encounter" onClick={handle('open-encounter')} />
        ) : null}
        {shouldShowPvsMenuItem(row.statusCode) ? <MenuItem icon="preview" label="View PVS" onClick={handle('view-pvs')} /> : null}
        {canViewBillingTab && shouldShowBillingTabMenuItem(row.statusCode) ? (
          <MenuItem icon="billing" label="Open 'Billing' Tab" onClick={handle('open-billing-tab')} />
        ) : null}
        {shouldShowViewRecurringSeriesMenuItem(row.isRecurring) ? (
          <MenuItem icon="recurring" label="View Recurring Series" onClick={handle('view-recurring-series')} />
        ) : null}
        {!isTerminal && !foldedIds.has('cancel') ? <MenuItem icon="cancelled" label="Cancel appointment" tone="error" onClick={handle('cancel')} /> : null}
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}

function MenuItem({
  icon,
  glyph,
  label,
  tone = 'default',
  disabled = false,
  folded = false,
  onClick,
}: {
  icon?: ApptGlyph;
  /** A Lucide glyph given directly. */
  glyph?: LucideIcon;
  label: string;
  tone?: 'default' | 'error';
  disabled?: boolean;
  /** One of the row's actions folded under the ⋯ (past the three inline icons). */
  folded?: boolean;
  onClick: (e: MouseEvent) => void;
}): ReactNode {
  const Glyph = glyph ?? APPT_GLYPH[icon ?? 'appointment'];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="cal-day-menu-item"
      data-tone={tone === 'error' ? 'error' : undefined}
      data-folded-row-action={folded ? '' : undefined}
    >
      <Glyph aria-hidden />
      <span>{label}</span>
    </button>
  );
}
